import type {
  StationState,
  PositionRotation,
  StationPipelineState,
  AmpStateFromSse,
} from "@/types"
import { create } from "zustand"

interface UnitState {
  _connectivity?: "online" | "degraded"
  _ws_connected?: boolean
  _degraded_since?: number
  motor_connected: boolean
  relays: Record<string, number>
  sdrs: Record<string, number>
  pan?: number
  tilt?: number
  tilt_offset?: number
  pan_offset?: number
  timer?: {
    sdrs: Record<string, number>
    relays: number
  }
  // Temperature and alarms per amplifier (keyed by SDR/amp id)
  amp_data?: Record<
    string,
    {
      temp_alarm: number
      VSWR_alarm: number
      temp: number
      alarm?: number
    }
  >
  // Unit temperature sensors by floor
  floor_temps?: Record<string, number>
  // Live amplifier state snapshot from unit SSE, keyed by amplifier config name
  amp_states?: Record<string, AmpStateFromSse | null>
  // Simple mode pipeline data per amplifier config
  pipeline?: StationPipelineState
  // Simple mode (simple start) data per amplifier config
  simpleMode?: StationPipelineState
}

interface UnitsStore {
  units: Record<string, UnitState>
  isConnected: boolean

  // Replace or update a single unit's state
  updateUnitState: (unitId: string, newState: StationState | null) => void

  // Update a specific relay value
  setRelayValue: (unitId: string, relayId: string, value: number) => void

  // Update a specific SDR value
  setSdrValue: (unitId: string, sdrId: string, value: number) => void

  // Set connection status
  setConnectionStatus: (connected: boolean) => void

  // Optional per-unit pan/tilt updates
  getPanTilt: (serial_number: string) => PositionRotation

  // Optional per-unit motor_connected
  getMotorConnected: (serial_number: string) => boolean

  // Getters
  getRelaysForUnit: (unitId: string) => Record<string, number>
  getUnitSdrs: (unitId: string) => Record<string, number>
  getAmplifierStatus: (
    unitId: string,
    ampfConfName: string,
  ) => number | undefined

  // Set amplifier status manually (for pending state during activation)
  setAmplifierStatus: (
    unitId: string,
    ampfConfigName: string,
    value: number,
  ) => void

  // UI-only PENDING overlay (key = "unitSerialNumber:configName"). Not telemetry.
  optimisticAmplifierOverrides: Record<string, number>
  optimisticAmplifierBaselines: Record<string, number>
  setOptimisticAmplifierOverrides: (next: Record<string, number>) => void
  upsertOptimisticAmplifierOverrides: (
    entries: {
      key: string
      value: number
      baseline?: number
    }[],
  ) => void
  clearOptimisticAmplifierOverrides: (keys: string[]) => void

  // Pipeline getter
  getPipelineData: (unitId: string) => UnitState["pipeline"]
  getSimpleModeData: (unitId: string) => UnitState["simpleMode"]

  // Temperature getters
  getAmplifierTemp: (unitId: string, sdrId: string) => number | undefined
  getFloorTemps: (unitId: string) => Record<string, number>

  // Timer getters
  getSdrTimer: (unitId: string, sdrId: string) => number | undefined
  getRelaysTimer: (unitId: string) => number | undefined
  getAllTimers: (
    unitId: string,
  ) => { sdrs: Record<string, number>; relays: number } | undefined
}

const mergeRecordField = <T extends Record<string, unknown>>(
  incoming: T | undefined,
  previous: T | undefined,
  preserveOnEmpty: boolean,
): T => {
  if (!incoming) {
    return (previous || {}) as T
  }

  if (preserveOnEmpty && Object.keys(incoming).length === 0) {
    return (previous || {}) as T
  }

  if (preserveOnEmpty) {
    return { ...(previous || {}), ...incoming } as T
  }

  return { ...incoming }
}

const mergePanTilt = (
  incoming: unknown,
  previous: number | undefined,
): number | undefined => {
  if (incoming === null || typeof incoming === "undefined") {
    return previous
  }

  if (typeof incoming === "number" && Number.isFinite(incoming)) {
    return incoming
  }

  return previous
}

export const useUnitsStore = create<UnitsStore>((set, get) => ({
  units: {},
  isConnected: false,

  updateUnitState: (unitId, newState) => {
    if (newState === null) {
      set((state) => {
        const { [unitId]: _, ...rest } = state.units
        const prefix = `${unitId}:`
        const nextOverrides = { ...state.optimisticAmplifierOverrides }
        const nextBaselines = { ...state.optimisticAmplifierBaselines }
        for (const key of Object.keys(nextOverrides)) {
          if (!key.startsWith(prefix)) continue
          delete nextOverrides[key]
          delete nextBaselines[key]
        }
        return {
          units: rest,
          optimisticAmplifierOverrides: nextOverrides,
          optimisticAmplifierBaselines: nextBaselines,
        }
      })
      return
    }
    const previousUnit = get().units[unitId]
    const preserveTelemetry =
      newState._connectivity === "degraded" ||
      previousUnit?._connectivity === "degraded"

    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          _connectivity:
            newState._connectivity ?? state.units[unitId]?._connectivity,
          _ws_connected:
            newState._ws_connected ?? state.units[unitId]?._ws_connected,
          _degraded_since:
            newState._degraded_since ?? state.units[unitId]?._degraded_since,
          relays: mergeRecordField(
            newState.relays,
            state.units[unitId]?.relays,
            preserveTelemetry,
          ),
          sdrs: mergeRecordField(
            newState.sdrs,
            state.units[unitId]?.sdrs,
            preserveTelemetry,
          ),
          pan: mergePanTilt(newState.pan, state.units[unitId]?.pan),
          tilt: mergePanTilt(newState.tilt, state.units[unitId]?.tilt),
          tilt_offset: newState.tilt_offset ?? state.units[unitId]?.tilt_offset,
          pan_offset: newState.pan_offset ?? state.units[unitId]?.pan_offset,
          motor_connected:
            typeof newState.motor_connected === "boolean"
              ? newState.motor_connected
              : (state.units[unitId]?.motor_connected ?? false),

          amp_data: mergeRecordField(
            newState.amp_data,
            state.units[unitId]?.amp_data,
            preserveTelemetry,
          ),
          floor_temps: mergeRecordField(
            newState.floor_temps,
            state.units[unitId]?.floor_temps,
            preserveTelemetry,
          ),
          amp_states: mergeRecordField(
            newState.amp_states,
            state.units[unitId]?.amp_states,
            preserveTelemetry,
          ),
          pipeline: mergeRecordField(
            newState.pipeline,
            state.units[unitId]?.pipeline,
            preserveTelemetry,
          ),
          simpleMode: mergeRecordField(
            newState.simple_mode,
            state.units[unitId]?.simpleMode,
            preserveTelemetry,
          ),
        },
      },
    }))
  },

  setRelayValue: (unitId, relayId, value) =>
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          relays: {
            ...state.units[unitId]?.relays,
            [relayId]: value,
          },
        },
      },
    })),

  setSdrValue: (unitId, sdrId, value) =>
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          sdrs: {
            ...state.units[unitId]?.sdrs,
            [sdrId]: value,
          },
        },
      },
    })),

  setConnectionStatus: (connected) => set({ isConnected: connected }),

  getPanTilt: (serial_number: string) => {
    const unit = get().units[serial_number]

    return {
      pan: unit?.pan ?? 0,
      tilt: unit?.tilt ?? 0,
      tilt_offset: unit?.tilt_offset ?? 0,
      pan_offset: unit?.pan_offset ?? 0,
    }
  },

  getMotorConnected: (serial_number: string) => {
    const unit = get().units[serial_number]
    return unit?.motor_connected ?? false
  },

  getRelaysForUnit: (unitId) => {
    return get().units[unitId]?.relays || {}
  },

  getUnitSdrs: (unitId) => {
    // console.log("getting")

    return get().units[unitId]?.sdrs || {}
  },

  getAmplifierStatus: (unitId, ampfConfigName) => {
    const val = get().units[unitId]?.sdrs?.[ampfConfigName]
    // Debug log for amplifier status lookup
    return val
  },

  setAmplifierStatus: (
    unitId: string,
    ampfConfigName: string,
    value: number,
  ) => {
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          sdrs: { ...state.units[unitId]?.sdrs, [ampfConfigName]: value },
        },
      },
    }))
  },

  optimisticAmplifierOverrides: {},
  optimisticAmplifierBaselines: {},
  setOptimisticAmplifierOverrides: (next) =>
    set((state) => {
      const nextBaselines: Record<string, number> = {}
      for (const key of Object.keys(next)) {
        const baseline = state.optimisticAmplifierBaselines[key]
        if (typeof baseline === "number") {
          nextBaselines[key] = baseline
        }
      }
      return {
        optimisticAmplifierOverrides: next,
        optimisticAmplifierBaselines: nextBaselines,
      }
    }),
  upsertOptimisticAmplifierOverrides: (entries) =>
    set((state) => {
      if (entries.length === 0) return state
      const nextOverrides = { ...state.optimisticAmplifierOverrides }
      const nextBaselines = { ...state.optimisticAmplifierBaselines }
      for (const entry of entries) {
        nextOverrides[entry.key] = entry.value
        if (typeof entry.baseline === "number") {
          nextBaselines[entry.key] = entry.baseline
        } else {
          delete nextBaselines[entry.key]
        }
      }
      return {
        optimisticAmplifierOverrides: nextOverrides,
        optimisticAmplifierBaselines: nextBaselines,
      }
    }),
  clearOptimisticAmplifierOverrides: (keys) =>
    set((state) => {
      if (keys.length === 0) return state
      const nextOverrides = { ...state.optimisticAmplifierOverrides }
      const nextBaselines = { ...state.optimisticAmplifierBaselines }
      let changed = false
      for (const key of keys) {
        if (!(key in nextOverrides) && !(key in nextBaselines)) continue
        delete nextOverrides[key]
        delete nextBaselines[key]
        changed = true
      }
      if (!changed) return state
      return {
        optimisticAmplifierOverrides: nextOverrides,
        optimisticAmplifierBaselines: nextBaselines,
      }
    }),

  getPipelineData: (unitId) => {
    return get().units[unitId]?.pipeline
  },

  getSimpleModeData: (unitId) => {
    return get().units[unitId]?.simpleMode
  },

  getAmplifierTemp: (unitId, sdrId) => {
    const temp = get().units[unitId]?.amp_data?.[sdrId]?.temp

    return temp
  },

  getFloorTemps: (unitId) => {
    const temps = get().units[unitId]?.floor_temps || {}

    return temps
  },

  getSdrTimer: (unitId, sdrId) => {
    return get().units[unitId]?.timer?.sdrs?.[sdrId]
  },

  getRelaysTimer: (unitId) => {
    return get().units[unitId]?.timer?.relays
  },

  getAllTimers: (unitId) => {
    const unit = get().units[unitId]
    return unit?.timer
      ? {
          sdrs: unit.timer.sdrs,
          relays: unit.timer.relays,
        }
      : undefined
  },
}))
